import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  AlertCircle,
  ArrowRight,
  Check,
  X,
  Eye,
  ShieldCheck,
  GripVertical,
  Search,
  Sparkles,
} from 'lucide-react';
import { BulkReassignModal } from './components/BulkReassignModal';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { ModalShell } from '@/components/ModalShell';
import { EmptyState, Skeleton } from '@/components/chassis/States';
import { apiCall } from '@/lib/api-client';
import { clientsApi, type PatientSearchResult } from '@/features/clients/clients.api';
import { toast } from '@/stores/toast.store';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import { useT, type StringKey } from '@/i18n';

// ---------------------------------------------------------------------------
// F5-T6b · Admin 05 — Reasignación de agentes (contrato 2026-08-28).
//
// Una superficie, tres pestañas: Auto-rebalanceo · Manual por arrastre ·
// Masivo (solo admin — para supervisor la pestaña NO se dibuja; el endpoint
// /admin/reassign/bulk ya es @Roles('admin'), así muere el botón que daba
// 403). Los cuatro candados de plata están escritos EN la pantalla.
//
// COMPUERTA DEL PREVIEW: en Auto y Masivo, «Aplicar el movimiento» no se
// renderiza hasta que el preview corrió con los parámetros vigentes; cambiar
// cualquier parámetro (o de pestaña) lo hace desaparecer de nuevo.
//
// NECESITA-SERVER (dibujado en el contrato, sin endpoint hoy — NO se finge):
//   · Origen (radio-cards sobrecargados/desconectados), Destino (3 pills) y
//     Tope por agente (slider 20-120): POST /admin/reassign/preview auto solo
//     acepta autoOptions.respectLanguage. El motor decide el reparto.
//   · Salteados con razón EN el preview: el server los devuelve recién al
//     apply (skipped[]). Acá se muestran al aplicar, con nota honesta antes.
//   · Tarjetas bloqueadas con chip de razón en Manual: GET /admin/reassign/
//     clients excluye agotados/con-cita server-side (solo excludedCount).
//   · Estado del paciente («Parqueado»/«Con cita») y «Desbloquear y abrir» en
//     la búsqueda: /agents/me/clients/search no devuelve estado ni existe el
//     desbloqueo en el dominio de agentes.
//   · Auditoría del EVENTO de búsqueda global: hoy se audita abrir el perfil
//     (VIEW_CLIENT), no la búsqueda en sí (el copy del chip lo dice honesto).
// ---------------------------------------------------------------------------

type AgentStatus = 'online' | 'idle' | 'on_call' | 'offline';

interface AgentDashboardRow {
  id: string;
  name: string;
  email: string;
  languages: string[];
  active: boolean;
  pendingCount: number;
  inProgressCount: number;
  completedToday: number;
  dailyTarget: number;
  targetPercent: number;
  status: AgentStatus;
}

interface DashboardResponse {
  agents: AgentDashboardRow[];
  totals: {
    totalPending: number;
    totalInProgress: number;
    activeAgents: number;
    idealPerAgent: number;
  };
}

interface PlannedMove {
  clientId: string;
  fromAgentId: string;
  toAgentId: string;
  fromAgentName: string;
  toAgentName: string;
  clientLastNameMasked: string;
  clientLanguage: string | null;
}

interface PreviewResponse {
  moves: PlannedMove[];
  summary: {
    affectedClients: number;
    byAgent: Record<string, { receives: number; sends: number; finalLoad: number }>;
  };
}

interface AgentClientRow {
  id: string;
  assignmentId: string;
  lastNameMasked: string;
  cinSuffix: string;
  language: string | null;
  priority: number;
  status: string;
  assignedAt: string;
}

interface ClientsResponse {
  clients: AgentClientRow[];
  total: number;
  excludedCount?: number;
}

interface ApplyResult {
  applied: number;
  skipped?: Array<{ clientId: string; reason: string }>;
}

interface OrphanedOwnerRow {
  id: string;
  fullName: string;
  email: string;
  role: string;
  active: boolean;
  pendingCount: number;
}

type Tab = 'auto' | 'manual' | 'bulk' | 'orphaned';

/** Parámetros congelados al correr el preview — la compuerta compara contra esto. */
type PreviewCtx =
  | { origin: 'auto'; respectLanguage: boolean }
  // El multi-select del manual siempre reportó method='bulk' al server — se
  // conserva esa semántica (regla de la casa: no cambiar a quién/cómo mueve).
  | { origin: 'manual'; method: 'manual' | 'bulk' }
  | { origin: 'bulk'; fromId: string; toId: string; qty: number };

function methodOf(ctx: PreviewCtx): 'auto' | 'manual' | 'bulk' {
  if (ctx.origin === 'auto') return 'auto';
  if (ctx.origin === 'manual') return ctx.method;
  return 'bulk';
}

const dashboardKey = ['admin', 'reassign', 'dashboard'] as const;
const clientsKey = (agentId: string, language: string, status: string) =>
  ['admin', 'reassign', 'clients', agentId, language, status] as const;

const BTN_PRI =
  'inline-flex items-center justify-center gap-2 min-h-[44px] px-5 rounded-lg bg-nav-navy border-[1.5px] border-nav-navy text-white font-head font-bold text-[13.5px] hover:bg-nav-navyHover transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_GHOST =
  'inline-flex items-center justify-center gap-2 min-h-[44px] px-[18px] rounded-lg bg-white border-[1.5px] border-nav-line text-nav-navy font-head font-bold text-[13.5px] hover:bg-nav-softer hover:border-nav-faint transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
const CARD =
  'bg-white border-[1.5px] border-nav-card rounded-xl p-6';
const KICKER =
  'font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal';

export function ReassignPage(): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';

  const [tab, setTab] = useState<Tab>('auto');
  const [bulkToolOpen, setBulkToolOpen] = useState(false);

  const dashQ = useQuery<DashboardResponse>({
    queryKey: dashboardKey,
    queryFn: () => apiCall<DashboardResponse>('/admin/reassign/dashboard'),
    staleTime: 10_000,
  });

  const [respectLanguage, setRespectLanguage] = useState(true);
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [previewCtx, setPreviewCtx] = useState<PreviewCtx | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false); // modal — solo manual
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null);
  const [announce, setAnnounce] = useState('');

  // Masivo — mismo motor (preview→apply) que auto; el conjunto se elige a mano.
  const [blkFrom, setBlkFrom] = useState('');
  const [blkTo, setBlkTo] = useState('');
  const [blkQty, setBlkQty] = useState('');
  const [blkShort, setBlkShort] = useState<{ have: number; take: number } | null>(null);

  // Casos sin dueño activo — misma ManualPanel, origen fijo en un dueño no-agente.
  const [selectedOrphan, setSelectedOrphan] = useState<OrphanedOwnerRow | null>(null);
  const orphanedQ = useQuery<OrphanedOwnerRow[]>({
    queryKey: ['admin', 'reassign', 'orphaned-owners'] as const,
    queryFn: () => apiCall<OrphanedOwnerRow[]>('/admin/reassign/orphaned-owners'),
    enabled: tab === 'orphaned' && isAdmin,
  });

  const previewPanelRef = useRef<HTMLDivElement>(null);

  const resetPreview = (): void => {
    setPreviewData(null);
    setPreviewCtx(null);
    setPreviewOpen(false);
  };

  const switchTab = (next: Tab): void => {
    // Cambiar de pestaña descarta el preview (contrato, structure 2b).
    setTab(next);
    resetPreview();
    setApplyResult(null);
    setBlkShort(null);
    setSelectedOrphan(null);
  };

  const previewMutation = useMutation({
    mutationFn: (input: {
      ctx: PreviewCtx;
      moves?: Array<{ clientId: number; toAgentId: number }>;
    }) =>
      apiCall<PreviewResponse>('/admin/reassign/preview', {
        method: 'POST',
        body:
          input.ctx.origin === 'auto'
            ? { method: 'auto', autoOptions: { respectLanguage: input.ctx.respectLanguage } }
            : { method: methodOf(input.ctx), moves: input.moves },
      }),
    onSuccess: (data, variables) => {
      setPreviewData(data);
      setPreviewCtx(variables.ctx);
      setApplyResult(null);
      const receivers = new Set(data.moves.map((m) => m.toAgentId)).size;
      setAnnounce(t('f5t6b.rs.pv.aria', { m: data.moves.length, r: receivers }));
      if (variables.ctx.origin === 'manual') {
        if (data.moves.length === 0) {
          toast.info(t('f5t6b.rs.pv.noneTitle'), t('f5t6b.rs.pv.noneBody'));
        } else {
          setPreviewOpen(true);
        }
      } else {
        // El foco entra al panel de preview cuando aparece (contrato a11y).
        window.setTimeout(() => previewPanelRef.current?.focus(), 0);
      }
    },
    onError: (err: Error) => {
      toast.error(t('admin.reassign.result.error'), err.message);
    },
  });

  const applyMutation = useMutation({
    mutationFn: (input: {
      moves: Array<{ clientId: number; toAgentId: number }>;
      method: 'auto' | 'manual' | 'bulk';
    }) =>
      apiCall<ApplyResult>('/admin/reassign/apply', {
        method: 'POST',
        body: input,
      }),
    onSuccess: (data) => {
      toast.success(t('f5t6b.rs.ap.done', { n: data.applied }), '');
      setApplyResult(data);
      setAnnounce(t('f5t6b.rs.ap.done', { n: data.applied }));
      resetPreview();
      void qc.invalidateQueries({ queryKey: ['admin', 'reassign'] });
    },
    onError: (err: Error) => {
      toast.error(t('admin.reassign.result.error'), err.message);
    },
  });

  // Frescura de la compuerta: el preview vale solo con los parámetros vigentes.
  const previewFresh = useMemo(() => {
    if (!previewData || !previewCtx) return false;
    if (previewCtx.origin !== tab) return false;
    if (previewCtx.origin === 'auto') return previewCtx.respectLanguage === respectLanguage;
    if (previewCtx.origin === 'bulk') {
      return (
        previewCtx.fromId === blkFrom &&
        previewCtx.toId === blkTo &&
        String(previewCtx.qty) === blkQty.trim()
      );
    }
    return true;
  }, [previewData, previewCtx, tab, respectLanguage, blkFrom, blkTo, blkQty]);

  const handleAutoPreview = (): void => {
    previewMutation.mutate({ ctx: { origin: 'auto', respectLanguage } });
  };

  const handleManualPreview = (moves: Array<{ clientId: number; toAgentId: number }>): void => {
    if (moves.length === 0) {
      toast.info(t('admin.reassign.manual.noSelection'), '');
      return;
    }
    previewMutation.mutate({
      ctx: { origin: 'manual', method: moves.length > 1 ? 'bulk' : 'manual' },
      moves,
    });
  };

  const handleBulkPreview = async (): Promise<void> => {
    const qty = Math.min(parseInt(blkQty, 10) || 0, 200);
    if (!blkFrom || !blkTo || blkFrom === blkTo || qty <= 0) {
      toast.info(t('f5t6b.rs.blk.invalid'), '');
      return;
    }
    try {
      // El conjunto sale de los elegibles del origen (el server ya excluye
      // agotados y con-cita): la cantidad pedida es un techo, no una promesa.
      const res = await apiCall<ClientsResponse>(
        `/admin/reassign/clients?agentId=${blkFrom}&status=pending&limit=200`,
      );
      const chosen = res.clients.slice(0, qty);
      setBlkShort(chosen.length < qty ? { have: res.clients.length, take: chosen.length } : null);
      if (chosen.length === 0) {
        setPreviewData({ moves: [], summary: { affectedClients: 0, byAgent: {} } });
        setPreviewCtx({ origin: 'bulk', fromId: blkFrom, toId: blkTo, qty });
        return;
      }
      previewMutation.mutate({
        ctx: { origin: 'bulk', fromId: blkFrom, toId: blkTo, qty },
        moves: chosen.map((c) => ({ clientId: Number(c.id), toAgentId: Number(blkTo) })),
      });
    } catch (err) {
      toast.error(t('admin.reassign.result.error'), (err as Error).message);
    }
  };

  const handleApply = (): void => {
    if (!previewData || !previewCtx) return;
    applyMutation.mutate({
      method: methodOf(previewCtx),
      moves: previewData.moves.map((m) => ({
        clientId: Number(m.clientId),
        toAgentId: Number(m.toAgentId),
      })),
    });
  };

  const activeAgents = dashQ.data?.agents.filter((a) => a.active) ?? [];

  return (
    <div className="font-body text-nav-navy max-w-[1100px]">
      {/* aria-live: resultado del preview y del apply (contrato a11y). */}
      <p aria-live="polite" className="sr-only">{announce}</p>

      <header className="mb-6">
        <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-slate m-0">
          {t('f5t6b.rs.kicker')}
        </p>
        <h1 className="font-head text-[32px] font-extrabold tracking-[-0.025em] leading-tight mt-1 mb-2">
          {t('f5t6b.rs.title')}
        </h1>
        <p className="text-[15px] leading-relaxed text-nav-navy/90 m-0 max-w-[640px]">{t('f5t6b.rs.lead')}</p>
        <p className="mt-2 text-[13.5px] leading-relaxed text-nav-slate max-w-[640px] m-0">{t('f5t6b.rs.sub')}</p>
      </header>

      {dashQ.error && (
        <Alert variant="error" role="alert">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <AlertTitle>{t('admin.reassign.errors.dashboardLoad')}</AlertTitle>
            <AlertDescription>{(dashQ.error as Error).message}</AlertDescription>
          </div>
        </Alert>
      )}

      <div className="bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] px-7 pt-7 pb-8 animate-fade-up-slow motion-reduce:animate-none">
        {/* Candados de plata — escritos EN la pantalla, no en un manual. */}
        <div className="bg-nav-tealTint border-[1.5px] border-nav-teal rounded-xl px-[22px] py-5 text-nav-tealDark">
          <p className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase m-0 mb-3">
            {t('f5t6b.rs.locks.title')}
          </p>
          <div className="grid gap-x-6 gap-y-3.5 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
            {([1, 2, 3, 4] as const).map((i) => (
              <p key={i} className="m-0 text-[13.5px] leading-relaxed">
                <strong className="font-bold">{t(`f5t6b.rs.locks.${i}b` as StringKey)}</strong>{' '}
                {t(`f5t6b.rs.locks.${i}t` as StringKey)}
              </p>
            ))}
          </div>
        </div>

        {/* Pestañas segmentadas. Masivo NO se dibuja para supervisor. */}
        <div className="inline-flex flex-wrap items-center gap-[2px] bg-white border-[1.5px] border-nav-card rounded-[10px] p-[3px] my-[22px]">
          {(
            [
              { id: 'auto' as Tab, label: t('f5t6b.rs.tab.auto') },
              { id: 'manual' as Tab, label: t('f5t6b.rs.tab.manual') },
              ...(isAdmin ? [{ id: 'bulk' as Tab, label: t('f5t6b.rs.tab.bulk') }] : []),
              ...(isAdmin ? [{ id: 'orphaned' as Tab, label: t('f5t6b.rs.tab.orphaned') }] : []),
            ]
          ).map((tb) => (
            <button
              key={tb.id}
              type="button"
              aria-pressed={tab === tb.id}
              onClick={() => switchTab(tb.id)}
              className={cn(
                'inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg font-head font-bold text-[13px] transition-colors motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                tab === tb.id
                  ? tb.id === 'orphaned'
                    ? 'bg-nav-amber text-white hover:bg-nav-amber'
                    : 'bg-nav-navy text-white hover:bg-nav-navyHover'
                  : 'bg-transparent text-nav-slate hover:bg-nav-softer hover:text-nav-navy',
              )}
            >
              {tb.label}
              {tb.id === 'bulk' && (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 font-head text-[9.5px] font-bold uppercase tracking-[0.06em]',
                    tab === 'bulk' ? 'bg-white/20 text-white' : 'bg-nav-hair text-nav-slate',
                  )}
                >
                  {t('f5t6b.rs.tab.bulkChip')}
                </span>
              )}
              {tb.id === 'orphaned' && (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 font-head text-[9.5px] font-bold uppercase tracking-[0.06em]',
                    tab === 'orphaned' ? 'bg-white/20 text-white' : 'bg-nav-amberTint text-nav-amberText',
                  )}
                >
                  {t('f5t6b.rs.tab.orphanedChip')}
                </span>
              )}
            </button>
          ))}
        </div>

        {dashQ.isLoading && <PanelSkeleton />}

        {dashQ.data && tab === 'auto' && (
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(330px,1fr))]">
            <section className={CARD} aria-label={t('f5t6b.rs.auto.card1')}>
              <p className={cn(KICKER, 'm-0 mb-3')}>{t('f5t6b.rs.auto.card1')}</p>
              <p className="m-0 mb-4 text-[13.5px] leading-relaxed text-nav-slate">
                {t('f5t6b.rs.auto.motorNote')}
              </p>
              {/* CONSERVA (E-23): el respeto de idioma es el único parámetro
                  vivo del motor auto — el contrato no lo dibuja ni lo mata. */}
              <label className="flex items-center gap-2.5 min-h-[44px] text-[13.5px] text-nav-navy cursor-pointer">
                <Checkbox
                  checked={respectLanguage}
                  onChange={(e) => setRespectLanguage(e.target.checked)}
                />
                {t('admin.reassign.auto.respectLanguage')}
              </label>
            </section>

            <PreviewCard
              tRef={previewPanelRef}
              fresh={previewFresh}
              data={previewCtx?.origin === 'auto' ? previewData : null}
              pending={previewMutation.isPending}
              applying={applyMutation.isPending}
              onRun={handleAutoPreview}
              onDiscard={resetPreview}
              onApply={handleApply}
              applyResult={applyResult}
            />
          </div>
        )}

        {dashQ.data && tab === 'manual' && (
          <section className={CARD}>
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
              <p className={cn(KICKER, 'm-0')}>{t('f5t6b.rs.man.title')}</p>
              <p className="m-0 text-[13px] text-nav-slate">{t('f5t6b.rs.man.hint')}</p>
            </div>
            <p className="m-0 mb-4 max-w-[660px] text-[13.5px] leading-relaxed text-nav-slate">
              {t('f5t6b.rs.man.desc')}
            </p>
            <ManualPanel fromOptions={activeAgents} toOptions={activeAgents} onPreview={handleManualPreview} />
          </section>
        )}

        {dashQ.data && tab === 'bulk' && isAdmin && (
          <div className="space-y-4">
            <section className={CARD}>
              <p className={cn(KICKER, 'm-0 mb-2')}>{t('f5t6b.rs.blk.title')}</p>
              <p className="m-0 mb-5 max-w-[680px] text-[13.5px] leading-relaxed text-nav-slate">
                {t('f5t6b.rs.blk.desc')}
              </p>
              <div className="grid gap-x-5 gap-y-[18px] [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                <div>
                  <Label className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
                    {t('f5t6b.rs.blk.from')}
                  </Label>
                  <Select value={blkFrom} onChange={(e) => setBlkFrom(e.target.value)} className="min-h-[44px]">
                    <option value="" />
                    {activeAgents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.pendingCount})
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
                    {t('f5t6b.rs.blk.to')}
                  </Label>
                  <Select value={blkTo} onChange={(e) => setBlkTo(e.target.value)} className="min-h-[44px]">
                    <option value="" />
                    {activeAgents
                      .filter((a) => a.id !== blkFrom)
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} ({a.pendingCount})
                        </option>
                      ))}
                  </Select>
                </div>
                <div>
                  <Label className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
                    {t('f5t6b.rs.blk.qty')}
                  </Label>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    value={blkQty}
                    onChange={(e) => setBlkQty(e.target.value)}
                    aria-label={t('f5t6b.rs.blk.qty')}
                    className="w-full min-h-[44px] px-3 rounded-lg border-[1.5px] border-nav-line bg-white text-[13.5px] tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                  />
                  <p className="m-0 mt-1 text-[12px] text-nav-slate">{t('f5t6b.rs.blk.capHint')}</p>
                </div>
              </div>

              <div className="mt-5 flex items-start gap-3 bg-nav-amberTint border-[1.5px] border-nav-amber rounded-xl px-[18px] py-4 text-nav-amberText">
                <AlertCircle className="h-[17px] w-[17px] mt-0.5 shrink-0" aria-hidden="true" />
                <p className="m-0 text-[13.5px] leading-relaxed">{t('f5t6b.rs.blk.note')}</p>
              </div>

              {blkShort && (
                <p className="m-0 mt-3 text-[13px] text-nav-amberText" aria-live="polite">
                  {t('f5t6b.rs.blk.short', { have: blkShort.have, take: blkShort.take })}
                </p>
              )}

              <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                {/* CONSERVA (E-23): la herramienta masiva clásica (Cluster L,
                    POST /admin/reassign/bulk con motivo auditado y audiencia
                    agent/uploader) sigue viva — el contrato no la dibuja ni
                    la mata, y cubre lo que esta compuerta no (hasta 1000,
                    uploaders). */}
                <button type="button" className={BTN_GHOST} onClick={() => setBulkToolOpen(true)}>
                  {t('bulkReassign.button')}
                </button>
                <button
                  type="button"
                  className={BTN_GHOST}
                  onClick={() => {
                    setBlkFrom('');
                    setBlkTo('');
                    setBlkQty('');
                    setBlkShort(null);
                    resetPreview();
                  }}
                >
                  {t('f5t6b.rs.blk.clear')}
                </button>
                <button
                  type="button"
                  className={BTN_PRI}
                  onClick={() => void handleBulkPreview()}
                  disabled={previewMutation.isPending}
                >
                  {previewMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                      {t('f5t6b.rs.pv.running')}
                    </>
                  ) : (
                    t('f5t6b.rs.pv.run')
                  )}
                </button>
              </div>
            </section>

            <PreviewCard
              tRef={previewPanelRef}
              fresh={previewFresh}
              data={previewCtx?.origin === 'bulk' ? previewData : null}
              pending={previewMutation.isPending}
              applying={applyMutation.isPending}
              onRun={() => void handleBulkPreview()}
              onDiscard={resetPreview}
              onApply={handleApply}
              applyResult={applyResult}
              hideRunInEmpty
            />
          </div>
        )}

        {dashQ.data && tab === 'orphaned' && isAdmin && (
          <section className={CARD}>
            <p className={cn(KICKER, 'm-0 mb-2')}>{t('f5t6b.rs.orp.title')}</p>
            <div className="mt-3 mb-5 flex items-start gap-3 bg-nav-amberTint border-[1.5px] border-nav-amber rounded-xl px-[18px] py-4 text-nav-amberText">
              <AlertCircle className="h-[17px] w-[17px] mt-0.5 shrink-0" aria-hidden="true" />
              <p className="m-0 text-[13.5px] leading-relaxed">{t('f5t6b.rs.orp.note')}</p>
            </div>

            {!selectedOrphan ? (
              orphanedQ.isLoading ? (
                <div className="space-y-2 py-2" aria-busy="true">
                  <Skeleton className="h-[46px] w-full" />
                  <Skeleton className="h-[46px] w-full" />
                  <Skeleton className="h-[46px] w-full" />
                </div>
              ) : !orphanedQ.data || orphanedQ.data.length === 0 ? (
                <EmptyState
                  icon={<Eye className="h-[22px] w-[22px]" aria-hidden="true" />}
                  title={t('f5t6b.rs.orp.empty')}
                  body=""
                />
              ) : (
                <div className="border-[1.5px] border-nav-card rounded-[10px] bg-white overflow-hidden overflow-x-auto">
                  <table className="w-full text-[13.5px]">
                    <thead>
                      <tr className="bg-nav-soft border-b border-nav-card text-left">
                        <th className="px-3 py-2 font-head text-[11px] uppercase tracking-wide text-nav-slate">{t('f5t6b.rs.orp.colName')}</th>
                        <th className="px-3 py-2 font-head text-[11px] uppercase tracking-wide text-nav-slate">{t('f5t6b.rs.orp.colEmail')}</th>
                        <th className="px-3 py-2 font-head text-[11px] uppercase tracking-wide text-nav-slate">{t('f5t6b.rs.orp.colRole')}</th>
                        <th className="px-3 py-2 font-head text-[11px] uppercase tracking-wide text-nav-slate">{t('f5t6b.rs.orp.colActive')}</th>
                        <th className="px-3 py-2 font-head text-[11px] uppercase tracking-wide text-nav-slate text-right">{t('f5t6b.rs.orp.colPending')}</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {orphanedQ.data.map((o) => (
                        <tr key={o.id} className="border-b border-nav-hair last:border-b-0">
                          <td className="px-3 py-2.5 font-bold">{o.fullName}</td>
                          <td className="px-3 py-2.5 text-nav-slate">{o.email}</td>
                          <td className="px-3 py-2.5 text-nav-slate">{o.role}</td>
                          <td className="px-3 py-2.5">
                            {o.active ? t('f5t6b.rs.orp.active') : t('f5t6b.rs.orp.inactive')}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{o.pendingCount}</td>
                          <td className="px-3 py-2.5 text-right">
                            <button type="button" className={BTN_GHOST} onClick={() => setSelectedOrphan(o)}>
                              {t('f5t6b.rs.orp.reassignBtn')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <div className="space-y-3">
                <button type="button" className={BTN_GHOST} onClick={() => setSelectedOrphan(null)}>
                  {t('f5t6b.rs.orp.back')}
                </button>
                <ManualPanel
                  fromOptions={[
                    {
                      id: selectedOrphan.id,
                      name: selectedOrphan.fullName,
                      email: selectedOrphan.email,
                      languages: [],
                      active: selectedOrphan.active,
                      pendingCount: selectedOrphan.pendingCount,
                      inProgressCount: 0,
                      completedToday: 0,
                      dailyTarget: 0,
                      targetPercent: 0,
                      status: 'offline',
                    },
                  ]}
                  toOptions={activeAgents}
                  onPreview={handleManualPreview}
                />
              </div>
            )}
          </section>
        )}

        <div className="h-[22px]" aria-hidden="true" />

        <PatientSearchCard
          onOpen={(id) => navigate(`/clients/${encodeURIComponent(id)}`)}
        />
      </div>

      {/* CONSERVA (E-23): panorama del equipo (totales + tarjetas por agente)
          — vivo desde el redesign RA; el contrato ni lo dibuja ni lo mata. */}
      {dashQ.data && (
        <section className="mt-8">
          <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-slate mb-3 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            {t('f5t6b.rs.overview')}
          </p>
          <div className="space-y-3">
            <DashboardTotals totals={dashQ.data.totals} />
            <AgentCardsGrid agents={dashQ.data.agents} ideal={dashQ.data.totals.idealPerAgent} />
          </div>
        </section>
      )}

      {isAdmin && (
        <BulkReassignModal
          open={bulkToolOpen}
          audience="choose"
          onClose={() => setBulkToolOpen(false)}
          invalidateQueryKey={['admin', 'reassign', 'dashboard']}
        />
      )}

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        preview={previewCtx?.origin === 'manual' ? previewData : null}
        onApply={handleApply}
        applying={applyMutation.isPending}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview — la compuerta compartida de Auto y Masivo.
// ---------------------------------------------------------------------------

function PreviewCard({
  tRef,
  fresh,
  data,
  pending,
  applying,
  onRun,
  onDiscard,
  onApply,
  applyResult,
  hideRunInEmpty,
}: {
  tRef: React.RefObject<HTMLDivElement>;
  fresh: boolean;
  data: PreviewResponse | null;
  pending: boolean;
  applying: boolean;
  onRun: () => void;
  onDiscard: () => void;
  onApply: () => void;
  applyResult: ApplyResult | null;
  /** Masivo: «Correr preview» vive en la card de inputs, no en el estado vacío. */
  hideRunInEmpty?: boolean;
}): JSX.Element {
  const t = useT();

  const grouped = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, { from: string; to: string; toId: string; count: number }>();
    for (const m of data.moves) {
      const k = `${m.fromAgentName}→${m.toAgentName}`;
      const g = map.get(k) ?? { from: m.fromAgentName, to: m.toAgentName, toId: m.toAgentId, count: 0 };
      g.count += 1;
      map.set(k, g);
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [data]);

  const receivers = data ? new Set(data.moves.map((m) => m.toAgentId)).size : 0;

  return (
    <section
      ref={tRef}
      tabIndex={-1}
      className={cn(CARD, 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal')}
      aria-label={t('f5t6b.rs.auto.card2')}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className={cn(KICKER, 'm-0')}>{t('f5t6b.rs.auto.card2')}</p>
        {data && data.moves.length > 0 && (
          <span
            className={cn(
              'rounded-full border px-2.5 py-1 font-head text-[11px] font-bold',
              fresh
                ? 'bg-nav-sageTint text-nav-sageText border-nav-sage'
                : 'bg-nav-amberTint text-nav-amberText border-nav-amber',
            )}
          >
            {fresh ? t('f5t6b.rs.chip.fresh') : t('f5t6b.rs.chip.stale')}
          </span>
        )}
      </div>

      {pending ? (
        <div className="space-y-2 py-2" aria-busy="true">
          <Skeleton className="h-[68px] w-full" />
          <Skeleton className="h-[46px] w-full" />
          <Skeleton className="h-[46px] w-full" />
        </div>
      ) : !data ? (
        <>
          <EmptyState
            icon={<Eye className="h-[22px] w-[22px]" aria-hidden="true" />}
            title={t('f5t6b.rs.pv.emptyTitle')}
            body={t('f5t6b.rs.pv.emptyBody')}
            action={hideRunInEmpty ? undefined : { label: t('f5t6b.rs.pv.run'), onClick: onRun }}
          />
          {applyResult && <SkipBox result={applyResult} />}
        </>
      ) : data.moves.length === 0 ? (
        <EmptyState
          icon={<Eye className="h-[22px] w-[22px]" aria-hidden="true" />}
          title={t('f5t6b.rs.pv.noneTitle')}
          body={t('f5t6b.rs.pv.noneBody')}
          action={{ label: t('f5t6b.rs.pv.discard'), onClick: onDiscard }}
        />
      ) : (
        <div>
          <div className="flex flex-wrap gap-6 mb-4">
            <div>
              <p className="m-0 font-head text-[28px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">
                {data.moves.length}
              </p>
              <p className="m-0 mt-1 text-[12.5px] text-nav-slate">{t('f5t6b.rs.pv.moves')}</p>
            </div>
            <div>
              <p className="m-0 font-head text-[28px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">
                {receivers}
              </p>
              <p className="m-0 mt-1 text-[12.5px] text-nav-slate">{t('f5t6b.rs.pv.receivers')}</p>
            </div>
          </div>

          <ul className="m-0 p-0 list-none border-[1.5px] border-nav-card rounded-[10px] overflow-hidden">
            {grouped.map((g) => {
              const finalLoad = data.summary.byAgent[g.toId]?.finalLoad;
              return (
                <li
                  key={`${g.from}→${g.to}`}
                  className="flex items-center justify-between gap-3 px-4 py-[13px] border-b border-nav-hair last:border-b-0 bg-white"
                >
                  <span className="min-w-0">
                    <span className="block font-head text-[13px] font-bold truncate">
                      {g.from} → {g.to}
                    </span>
                    {finalLoad !== undefined && (
                      <span className="block text-[12.5px] text-nav-slate tabular-nums">
                        {t('f5t6b.rs.pv.staysAt', { n: finalLoad })}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 font-head text-[17px] font-extrabold tabular-nums">{g.count}</span>
                </li>
              );
            })}
          </ul>

          {/* NECESITA-SERVER: los salteados con razón dentro del preview — el
              server los calcula recién en el apply. Nota honesta mientras. */}
          <p className="m-0 mt-3 text-[12.5px] text-nav-slate">{t('f5t6b.rs.pv.skipNote')}</p>

          <div className="mt-5 flex items-center justify-end gap-2">
            <button type="button" className={BTN_GHOST} onClick={onDiscard}>
              {t('f5t6b.rs.pv.discard')}
            </button>
            {/* Con el preview vencido, el camino es volver a correrlo. */}
            {!fresh && (
              <button type="button" className={BTN_PRI} onClick={onRun}>
                {t('f5t6b.rs.pv.run')}
              </button>
            )}
            {/* COMPUERTA: «Aplicar» no existe si el preview no está al día. */}
            {fresh && (
              <button type="button" className={BTN_PRI} onClick={onApply} disabled={applying}>
                {applying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    {t('f5t6b.rs.pv.applying')}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" aria-hidden="true" />
                    {t('f5t6b.rs.pv.apply')}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/** Los salteados del apply, uno por uno con su razón en palabras (candado 3). */
function SkipBox({ result }: { result: ApplyResult }): JSX.Element | null {
  const t = useT();
  const skipped = result.skipped ?? [];
  if (skipped.length === 0) return null;
  return (
    <div className="mt-4 bg-nav-amberTint border-[1.5px] border-nav-amber rounded-xl px-[18px] py-4 text-nav-amberText">
      <p className="m-0 mb-2 font-head text-[10.5px] font-bold tracking-[0.12em] uppercase">
        {t('f5t6b.rs.ap.skipTitle', { n: skipped.length })}
      </p>
      <ul className="m-0 p-0 list-none space-y-[9px]">
        {skipped.map((s) => (
          <li key={s.clientId} className="flex items-center justify-between gap-3 text-[13px]">
            <span className="tabular-nums">{t('f5t6b.rs.ap.skipPatient', { id: s.clientId })}</span>
            <span className="font-head font-bold text-[12px]">
              {s.reason === 'EXHAUSTED'
                ? t('f5t6b.rs.skip.EXHAUSTED')
                : s.reason === 'HAS_SCHEDULED_BOOKING'
                  ? t('f5t6b.rs.skip.HAS_SCHEDULED_BOOKING')
                  : s.reason}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Búsqueda de paciente — común a las 3 pestañas. Identificadores completos
// (CIN o teléfono), sin búsqueda parcial por nombre (murió con el contrato).
// ---------------------------------------------------------------------------

function PatientSearchCard({ onOpen }: { onOpen: (clientId: string) => void }): JSX.Element {
  const t = useT();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), 400);
    return () => window.clearTimeout(id);
  }, [query]);

  const active = debounced.length >= 3;
  const searchQ = useQuery<PatientSearchResult[]>({
    queryKey: ['f5t6b', 'reassign', 'patient-search', debounced],
    queryFn: ({ signal }) => clientsApi.searchPatients(debounced, 20, signal),
    enabled: active,
    staleTime: 30_000,
  });

  const results = searchQ.data ?? [];

  return (
    <section className={CARD} aria-label={t('f5t6b.rs.se.title')}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className={cn(KICKER, 'm-0')}>{t('f5t6b.rs.se.title')}</p>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-nav-teal/30 bg-nav-tealTint px-[11px] py-[5px] font-head text-[11.5px] font-bold text-nav-tealDark">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          {t('f5t6b.rs.se.audit')}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nav-faint"
            aria-hidden="true"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('f5t6b.rs.se.ph')}
            aria-label={t('f5t6b.rs.se.aria')}
            className="w-full min-h-[44px] pl-9 pr-3 rounded-lg border-[1.5px] border-nav-line bg-white text-[13.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          />
        </div>
        <button
          type="button"
          className={BTN_GHOST}
          onClick={() => {
            setQuery('');
            setDebounced('');
          }}
        >
          {t('f5t6b.rs.se.clear')}
        </button>
      </div>

      {active && (
        <div className="mt-3 border-[1.5px] border-nav-card rounded-[10px] overflow-hidden" aria-live="polite">
          {searchQ.isLoading ? (
            <p className="m-0 px-4 py-4 text-[13px] text-nav-slate">{t('f5t6b.rs.se.searching')}</p>
          ) : results.length === 0 ? (
            <EmptyState
              icon={<Search className="h-5 w-5" aria-hidden="true" />}
              title={t('f5t6b.rs.se.emptyTitle')}
              body={t('f5t6b.rs.se.emptyBody')}
            />
          ) : (
            <ul className="m-0 p-0 list-none">
              {results.map((r) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-3 min-h-[60px] px-4 py-2.5 border-b border-nav-hair last:border-b-0 bg-white"
                >
                  <span className="min-w-0">
                    <span className="block font-head text-[13.5px] font-bold truncate">
                      {r.firstName} {r.lastName}
                    </span>
                    <span className="block text-[12.5px] text-nav-slate tabular-nums">
                      {t('f5t6b.rs.se.id', { last: r.cinLast6 })}
                      {r.dob ? ` · ${r.dob}` : ''}
                    </span>
                  </span>
                  {/* NECESITA-SERVER: estado del paciente («Parqueado», «Con
                      cita») y «Desbloquear y abrir» — sin endpoint hoy. Abrir
                      el perfil queda auditado (VIEW_CLIENT). */}
                  <button
                    type="button"
                    onClick={() => onOpen(r.id)}
                    className="inline-flex items-center min-h-[44px] px-[14px] rounded-lg bg-white border-[1.5px] border-nav-teal text-nav-tealDark font-head text-[12.5px] font-bold hover:bg-nav-tealTint transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                  >
                    {t('f5t6b.rs.se.open')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <p className="m-0 mt-3 text-[12.5px] leading-relaxed text-nav-slate">
        {active ? t('f5t6b.rs.se.noteActive') : t('f5t6b.rs.se.noteIdle')}
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Panorama (CONSERVA) — totales + tarjetas de agentes del redesign RA.
// ---------------------------------------------------------------------------

function DashboardTotals({ totals }: { totals: DashboardResponse['totals'] }): JSX.Element {
  const t = useT();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <TotalTile label={t('admin.reassign.dashboard.totalPending')} value={totals.totalPending} />
      <TotalTile label={t('admin.reassign.dashboard.totalInProgress')} value={totals.totalInProgress} />
      <TotalTile label={t('admin.reassign.dashboard.activeAgents')} value={totals.activeAgents} />
      <TotalTile label={t('admin.reassign.dashboard.idealPerAgent')} value={totals.idealPerAgent} />
    </div>
  );
}

function TotalTile({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded-xl border-[1.5px] border-nav-card bg-white p-4">
      <p className="font-head text-[10px] font-bold uppercase tracking-[0.12em] text-nav-slate m-0">{label}</p>
      <p className="font-head text-[26px] font-extrabold tabular-nums mt-1 m-0">{value}</p>
    </div>
  );
}

function AgentCardsGrid({
  agents,
  ideal,
}: {
  agents: AgentDashboardRow[];
  ideal: number;
}): JSX.Element {
  const t = useT();
  const overThreshold = Math.max(1, Math.ceil(ideal * 1.2));
  const underThreshold = Math.max(0, Math.floor(ideal * 0.5));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {agents.map((a) => {
        let load: 'over' | 'under' | 'balanced' = 'balanced';
        if (a.active && a.pendingCount > overThreshold) load = 'over';
        else if (a.active && a.pendingCount < underThreshold) load = 'under';

        const loadLabel =
          load === 'over'
            ? t('admin.reassign.agent.overloaded')
            : load === 'under'
              ? t('admin.reassign.agent.underloaded')
              : t('admin.reassign.agent.balanced');

        return (
          <div
            key={a.id}
            className={cn(
              'rounded-xl border-[1.5px] bg-white p-4 flex flex-col gap-3',
              a.active ? 'border-nav-card' : 'border-nav-card opacity-70',
            )}
          >
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="h-[30px] w-[30px] rounded-full flex items-center justify-center font-head text-[11px] font-bold shrink-0 bg-nav-navy text-white"
              >
                {initials(a.name)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-head text-[13.5px] font-bold truncate m-0">{a.name}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <StatusPill status={a.status} active={a.active} />
                  {a.languages.map((l) => (
                    <span
                      key={l}
                      className="font-head text-[10px] font-bold uppercase tracking-wide bg-nav-hair text-nav-slate border border-nav-card rounded px-1.5 py-0.5"
                    >
                      {l}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-end justify-between">
              <div>
                <p className="font-head text-[10px] font-bold uppercase tracking-[0.12em] text-nav-slate m-0">
                  {t('admin.reassign.agent.pendingCount')}
                </p>
                <p className="font-head text-[26px] font-extrabold tabular-nums leading-none mt-1 m-0">
                  {a.pendingCount}
                </p>
                {a.inProgressCount > 0 && (
                  <p className="text-[11px] text-nav-slate mt-1 m-0">
                    +{a.inProgressCount} {t('admin.reassign.agent.inProgress')}
                  </p>
                )}
              </div>
              <span
                className={cn(
                  'rounded-full border px-2.5 py-1 font-head text-[10px] font-bold uppercase tracking-[0.06em]',
                  load === 'over'
                    ? 'bg-nav-amberTint text-nav-amberText border-nav-amber/60'
                    : load === 'under'
                      ? 'bg-nav-hair text-nav-slate border-nav-card'
                      : 'bg-nav-sageTint text-nav-sageText border-nav-sage',
                )}
              >
                {loadLabel}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusPill({ status, active }: { status: AgentStatus; active: boolean }): JSX.Element {
  const t = useT();
  if (!active) {
    return (
      <span className="font-head text-[10px] font-bold uppercase tracking-wide bg-nav-hair text-nav-slate border border-nav-card rounded px-1.5 py-0.5">
        {t('admin.reassign.agent.inactive')}
      </span>
    );
  }
  const tone: Record<AgentStatus, string> = {
    online: 'bg-nav-sageTint text-nav-sageText border-nav-sage',
    idle: 'bg-nav-amberTint text-nav-amberText border-nav-amber/60',
    on_call: 'bg-nav-tealTint text-nav-tealDark border-nav-teal/40',
    offline: 'bg-nav-hair text-nav-slate border-nav-card',
  };
  return (
    <span className={cn('font-head text-[10px] font-bold uppercase tracking-wide border rounded px-1.5 py-0.5', tone[status])}>
      {t(`admin.reassign.agent.statusLabel.${status}` as StringKey)}
    </span>
  );
}

function PanelSkeleton(): JSX.Element {
  return (
    <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(330px,1fr))]" aria-busy="true">
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual — dos carteras (origen elegible / destino), arrastre + selección con
// teclado. La búsqueda por apellido parcial MURIÓ con el contrato (dies #4):
// era la que hacía posible pasear por la base sin ID.
// ---------------------------------------------------------------------------

function ManualPanel({
  fromOptions,
  toOptions,
  onPreview,
}: {
  fromOptions: AgentDashboardRow[];
  toOptions: AgentDashboardRow[];
  onPreview: (moves: Array<{ clientId: number; toAgentId: number }>) => void;
}): JSX.Element {
  const t = useT();
  const [fromId, setFromId] = useState<string>(fromOptions[0]?.id ?? '');
  const [toId, setToId] = useState<string>(toOptions[1]?.id ?? toOptions[0]?.id ?? '');
  const [language, setLanguage] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelected(new Set());
  }, [fromId, language, statusFilter]);

  const fromQ = useQuery<ClientsResponse>({
    queryKey: clientsKey(fromId, language, statusFilter),
    queryFn: () =>
      apiCall<ClientsResponse>(
        `/admin/reassign/clients?agentId=${fromId}` +
          (language ? `&language=${language}` : '') +
          (statusFilter ? `&status=${statusFilter}` : '') +
          `&limit=200`,
      ),
    enabled: !!fromId,
  });

  const toQ = useQuery<ClientsResponse>({
    queryKey: clientsKey(toId, '', ''),
    queryFn: () => apiCall<ClientsResponse>(`/admin/reassign/clients?agentId=${toId}&limit=200`),
    enabled: !!toId,
  });

  const fromClients = fromQ.data?.clients ?? [];
  const fromTotal = fromQ.data?.total ?? fromClients.length;
  const toClients = toQ.data?.clients ?? [];

  // R7 (encargo CEO): «pasar todos» — TODO lo elegible del origen al
  // destino, por tandas de 200 (el tope del fetch), con confirmacion del
  // numero exacto ANTES, progreso visible y verificacion final. Cada tanda
  // re-lista elegibles frescos (el server excluye agotados y con-cita en el
  // listado — la exclusion viaja gratis en cada vuelta).
  const [allBusy, setAllBusy] = useState(false);
  const [allProg, setAllProg] = useState<{ done: number; total: number } | null>(null);
  const [allConfirm, setAllConfirm] = useState(false);
  const qcAll = useQueryClient();
  const pasarTodos = async (): Promise<void> => {
    if (!fromId || !toId || fromId === toId || allBusy) return;
    setAllBusy(true);
    setAllConfirm(false);
    let done = 0;
    const objetivo = fromTotal;
    setAllProg({ done: 0, total: objetivo });
    try {
      // tandas: el listado se achica solo a medida que el apply mueve
      for (;;) {
        const lote = await apiCall<ClientsResponse>(
          `/admin/reassign/clients?agentId=${fromId}` +
            (language ? `&language=${language}` : '') +
            (statusFilter ? `&status=${statusFilter}` : '') +
            `&limit=200`,
        );
        if (lote.clients.length === 0) break;
        await apiCall('/admin/reassign/apply', {
          method: 'POST',
          body: {
            method: 'bulk',
            moves: lote.clients.map((c) => ({ clientId: Number(c.id), toAgentId: Number(toId) })),
          },
        });
        done += lote.clients.length;
        setAllProg({ done, total: Math.max(objetivo, done) });
        if (done > 5000) break; // freno de seguridad absurdo-alto
      }
      // verificacion final: el origen tiene que quedar en 0 elegibles
      const resto = await apiCall<ClientsResponse>(
        `/admin/reassign/clients?agentId=${fromId}` +
          (language ? `&language=${language}` : '') +
          (statusFilter ? `&status=${statusFilter}` : '') +
          `&limit=1`,
      );
      if ((resto.total ?? resto.clients.length) === 0) {
        toast.success(t('f5r7.all.done', { n: done }), '');
      } else {
        toast.info(t('f5r7.all.partial', { n: done, resto: resto.total }), '');
      }
    } catch (err) {
      toast.error(t('admin.reassign.result.error'), (err as Error).message);
    } finally {
      setAllBusy(false);
      setAllProg(null);
      setSelected(new Set());
      void qcAll.invalidateQueries({ queryKey: ['admin'], refetchType: 'all' });
      void fromQ.refetch();
      void toQ.refetch();
    }
  };
  const fromAgent = fromOptions.find((a) => a.id === fromId);
  const toAgent = toOptions.find((a) => a.id === toId);

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allChecked = fromClients.length > 0 && fromClients.every((c) => selected.has(c.id));
  const toggleAll = (): void => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(fromClients.map((c) => c.id)));
  };

  const handleDrop = (clientIds: string[]): void => {
    if (!toId || clientIds.length === 0) return;
    onPreview(clientIds.map((id) => ({ clientId: Number(id), toAgentId: Number(toId) })));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Cartera de ORIGEN */}
      <div className="rounded-xl border-[1.5px] border-nav-card bg-nav-softer px-3.5 py-4 space-y-2.5">
        <ColumnHeader agent={fromAgent} label={t('admin.reassign.manual.fromLabel')} loadText={t} />
        <Select value={fromId} onChange={(e) => setFromId(e.target.value)} className="min-h-[44px]">
          {fromOptions.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.pendingCount})
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wide">{t('admin.reassign.filter.language')}</Label>
            <Select value={language} onChange={(e) => setLanguage(e.target.value)} className="min-h-[44px]">
              <option value="">{t('admin.reassign.filter.languageAny')}</option>
              <option value="en">EN</option>
              <option value="es">ES</option>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide">{t('admin.reassign.filter.status')}</Label>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="min-h-[44px]">
              <option value="pending">{t('admin.reassign.filter.statusPending')}</option>
              <option value="in_progress">{t('admin.reassign.filter.statusInProgress')}</option>
              <option value="">{t('admin.reassign.filter.statusAll')}</option>
            </Select>
          </div>
        </div>

        {/* NECESITA-SERVER: las tarjetas bloqueadas con chip de razón — el
            server excluye agotados/con-cita y solo cuenta (excludedCount). */}
        <p className="text-[12px] text-nav-slate leading-snug m-0">
          {t('f5t6b.rs.man.excluded')}
          {fromQ.data?.excludedCount
            ? ` ${t('f5t6b.rs.man.excludedCount', { n: fromQ.data.excludedCount })}`
            : ''}
        </p>
        <ClientList
          clients={fromClients}
          loading={fromQ.isLoading}
          selected={selected}
          onToggle={toggle}
          allChecked={allChecked}
          onToggleAll={toggleAll}
          emptyHint={t('admin.reassign.manual.fromEmpty')}
          total={fromTotal}
        />
      </div>

      {/* Cartera de DESTINO */}
      <div className="rounded-xl border-[1.5px] border-nav-card bg-nav-softer px-3.5 py-4 space-y-2.5">
        <ColumnHeader agent={toAgent} label={t('admin.reassign.manual.toLabel')} loadText={t} />
        <Select value={toId} onChange={(e) => setToId(e.target.value)} className="min-h-[44px]">
          {toOptions.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.pendingCount})
            </option>
          ))}
        </Select>

        <div className="flex items-center justify-end gap-2 pb-1">
          {/* Alternativa por teclado al arrastre (contrato a11y). */}
          <button
            type="button"
            className={BTN_PRI}
            disabled={selected.size === 0 || !toId || fromId === toId}
            onClick={() => handleDrop(Array.from(selected))}
          >
            {t('admin.reassign.manual.moveSelected', { count: selected.size })}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={BTN_GHOST}
            disabled={!fromId || !toId || fromId === toId || allBusy || fromTotal === 0}
            onClick={() => setAllConfirm(true)}
          >
            {allBusy && allProg
              ? t('f5r7.all.progress', { done: allProg.done, total: allProg.total })
              : t('f5r7.all.cta', { n: fromTotal })}
          </button>
        </div>
        {/* R7: confirmacion con el numero exacto ANTES (plata de operacion). */}
        {allConfirm && (
          <div className="rounded-[10px] border-[1.5px] border-nav-amber bg-nav-amberTint px-4 py-3 mb-3">
            <p className="m-0 mb-2 text-[13px] text-nav-amberText font-head font-bold">
              {t('f5r7.all.confirmT', { n: fromTotal })}
            </p>
            <p className="m-0 mb-2.5 text-[12.5px] text-nav-amberText">
              {t('f5r7.all.confirmB')}
            </p>
            <div className="flex gap-2">
              <button type="button" className={BTN_PRI} onClick={() => void pasarTodos()}>
                {t('f5r7.all.confirmGo', { n: fromTotal })}
              </button>
              <button type="button" className={BTN_GHOST} onClick={() => setAllConfirm(false)}>
                {t('f5r7.all.confirmNo')}
              </button>
            </div>
          </div>
        )}

        <DropTarget
          onDrop={(ids) => {
            if (fromId === toId) {
              toast.info(t('admin.reassign.manual.sameAgent'), '');
              return;
            }
            handleDrop(ids);
          }}
          clients={toClients}
          loading={toQ.isLoading}
        />
      </div>
    </div>
  );
}

function ColumnHeader({
  agent,
  label,
  loadText,
}: {
  agent: AgentDashboardRow | undefined;
  label: string;
  loadText: ReturnType<typeof useT>;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden="true"
        className="h-[30px] w-[30px] rounded-full flex items-center justify-center font-head text-[11px] font-bold shrink-0 bg-nav-navy text-white"
      >
        {agent ? initials(agent.name) : '—'}
      </span>
      <span className="flex-1 min-w-0 font-head text-[13.5px] font-bold truncate">{label}</span>
      {agent && (
        <span className="shrink-0 rounded-full border border-nav-card bg-nav-hair px-[9px] py-[3px] font-head text-[12px] font-bold text-nav-slate tabular-nums">
          {loadText('f5t6b.rs.man.load', { n: agent.pendingCount })}
        </span>
      )}
    </div>
  );
}

function ClientList({
  clients,
  loading,
  selected,
  onToggle,
  allChecked,
  onToggleAll,
  emptyHint,
  total,
}: {
  clients: AgentClientRow[];
  loading: boolean;
  selected: Set<string>;
  onToggle: (id: string) => void;
  allChecked: boolean;
  onToggleAll: () => void;
  emptyHint: string;
  /** R7: total real del server (para decir «200 de 480» honesto). */
  total?: number;
}): JSX.Element {
  const t = useT();
  return (
    <div className="border-[1.5px] border-nav-card rounded-[10px] bg-white overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-nav-card bg-nav-soft min-h-[44px]">
        <Checkbox
          checked={allChecked}
          onChange={onToggleAll}
          aria-label={t('admin.reassign.manual.selectAll')}
        />
        <span className="text-xs text-nav-slate">
          {clients.length} {t('admin.reassign.manual.clients')}
          {typeof total === 'number' && total > clients.length
            ? ' ' + t('f5r7.all.ofTotal', { total })
            : ''}
        </span>
        <span className="text-[10px] text-nav-faint ml-auto tabular-nums">
          {selected.size > 0 && t('admin.reassign.manual.selectedCount', { count: selected.size })}
        </span>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-3 space-y-2" aria-busy="true">
            <Skeleton className="h-[56px] w-full" />
            <Skeleton className="h-[56px] w-full" />
            <Skeleton className="h-[56px] w-full" />
          </div>
        ) : clients.length === 0 ? (
          <div className="p-4 text-center text-nav-slate text-sm">{emptyHint}</div>
        ) : (
          <ul className="divide-y divide-nav-hair m-0 p-0 list-none">
            {clients.map((c) => {
              const checked = selected.has(c.id);
              return (
                <li
                  key={c.id}
                  draggable
                  onDragStart={(e) => {
                    const ids = checked && selected.size > 0 ? Array.from(selected) : [c.id];
                    e.dataTransfer.setData('text/plain', JSON.stringify(ids));
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2.5 min-h-[56px] text-sm cursor-grab active:cursor-grabbing',
                    checked ? 'bg-nav-tealTint/50' : 'hover:bg-nav-softer',
                  )}
                >
                  <GripVertical className="h-4 w-4 text-nav-faint shrink-0" aria-hidden="true" />
                  <Checkbox checked={checked} onChange={() => onToggle(c.id)} />
                  <div className="flex-1 min-w-0">
                    <p className="font-head text-[13px] font-bold truncate m-0">
                      {c.lastNameMasked}{' '}
                      <span className="text-nav-slate font-normal font-body tabular-nums">#{c.cinSuffix}</span>
                    </p>
                    <p className="text-[12px] text-nav-slate tabular-nums m-0">
                      {c.language ? c.language.toUpperCase() : '—'} ·{' '}
                      {c.status === 'pending'
                        ? t('admin.reassign.filter.statusPending')
                        : t('admin.reassign.filter.statusInProgress')}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function DropTarget({
  onDrop,
  clients,
  loading,
}: {
  onDrop: (ids: string[]) => void;
  clients: AgentClientRow[];
  loading: boolean;
}): JSX.Element {
  const t = useT();
  const [hover, setHover] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        const raw = e.dataTransfer.getData('text/plain');
        try {
          const ids = JSON.parse(raw);
          if (Array.isArray(ids)) onDrop(ids.map(String));
        } catch {
          // ignore
        }
      }}
      className={cn(
        'border-2 border-dashed rounded-[10px] transition-colors motion-reduce:transition-none overflow-hidden',
        // Confirmación visual del destino: borde teal (contrato a11y).
        hover ? 'border-nav-teal bg-nav-tealTint/40' : 'border-nav-card bg-white',
      )}
    >
      <div className="px-3 py-2 border-b border-nav-card bg-nav-soft text-xs text-nav-slate min-h-[44px] flex items-center">
        {t('admin.reassign.manual.dropHere')} · {clients.length} {t('admin.reassign.manual.clients')}
      </div>
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-3 space-y-2" aria-busy="true">
            <Skeleton className="h-[40px] w-full" />
            <Skeleton className="h-[40px] w-full" />
          </div>
        ) : clients.length === 0 ? (
          <div className="p-4 text-center text-nav-slate text-sm">
            {t('admin.reassign.manual.toEmpty')}
          </div>
        ) : (
          <ul className="divide-y divide-nav-hair m-0 p-0 list-none">
            {clients.map((c) => (
              <li key={c.id} className="px-3 py-2 text-sm flex items-center gap-2">
                <span className="font-head text-[13px] font-bold">{c.lastNameMasked}</span>
                <span className="text-nav-slate tabular-nums">#{c.cinSuffix}</span>
                <span className="ml-auto text-[10px] uppercase text-nav-faint">{c.language ?? 'en'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview modal — SOLO manual. CONSERVA: el apply vivo se conserva con su
// preview (regla de la casa); el contrato pedía manual sin preview y esa
// decisión sube al CEO (declarada en el reporte del draft).
// ---------------------------------------------------------------------------

function PreviewModal({
  open,
  onClose,
  preview,
  onApply,
  applying,
}: {
  open: boolean;
  onClose: () => void;
  preview: PreviewResponse | null;
  onApply: () => void;
  applying: boolean;
}): JSX.Element | null {
  const t = useT();
  if (!preview) return null;
  return (
    <ModalShell open={open} onClose={onClose} ariaLabelledBy="reassign-preview-title">
      <div className="flex items-center justify-between border-b border-nav-card px-5 py-4 shrink-0">
        <h2 id="reassign-preview-title" className="font-head text-lg font-bold text-nav-navy">
          {t('admin.reassign.preview.title')}
        </h2>
        <button
          onClick={onClose}
          aria-label={t('f5t6b.au.m.close')}
          className="h-11 w-11 min-h-[44px] inline-flex items-center justify-center rounded-lg hover:bg-nav-softer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
        >
          <X className="h-4 w-4 text-nav-slate" aria-hidden="true" />
        </button>
      </div>

      <div className="overflow-y-auto px-5 py-4 flex-1">
        <p className="text-sm text-nav-slate mb-3">
          {t('admin.reassign.preview.summary', {
            count: preview.summary.affectedClients,
            method: 'manual',
          })}
        </p>

        <ul className="divide-y divide-nav-hair border-[1.5px] border-nav-card rounded-[10px] bg-white m-0 p-0 list-none">
          {preview.moves.map((m) => (
            <li key={m.clientId} className="px-3 py-2.5 text-sm flex items-center gap-2">
              <span className="font-head font-bold text-nav-navy min-w-0 truncate">
                {m.clientLastNameMasked}
              </span>
              <span className="font-head text-[10px] font-bold uppercase rounded px-1.5 py-0.5 border bg-nav-hair text-nav-slate border-nav-card">
                {m.clientLanguage ?? 'en'}
              </span>
              <span className="ml-auto text-xs text-nav-slate flex items-center gap-1">
                <span>{m.fromAgentName}</span>
                <ArrowRight className="h-3 w-3 text-nav-faint" aria-hidden="true" />
                <span className="font-head font-bold text-nav-tealDark">{m.toAgentName}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-nav-card px-5 py-3 shrink-0">
        <button type="button" className={BTN_GHOST} onClick={onClose}>
          {t('admin.reassign.preview.cancel')}
        </button>
        <button
          type="button"
          className={BTN_PRI}
          onClick={onApply}
          disabled={preview.moves.length === 0 || applying}
        >
          {applying ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              {t('f5t6b.rs.pv.applying')}
            </>
          ) : (
            <>
              <Check className="h-4 w-4" aria-hidden="true" />
              {t('admin.reassign.preview.apply')}
            </>
          )}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function initials(name: string): string {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length === 0) return '??';
  const a = parts[0]?.charAt(0) ?? '';
  const b = parts[parts.length - 1]?.charAt(0) ?? '';
  return (a + b).toUpperCase().slice(0, 2);
}
